// file: src/pages/LandlordLookup.jsx
import React, { useRef, useEffect, useState } from "react";
import { lookupPlateLandlord } from "../api/landlordApi";

import { listDoNotTow } from "../api/landlordApi";
import Loader from "../components/Loader";

/* Brand */
const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

function clsx(...p) {
  return p.filter(Boolean).join(" ");
}

/* Utilities */
function fmtPartsTZ(iso, timeZone) {
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString(undefined, {
        timeZone: timeZone || undefined,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
      time: d.toLocaleTimeString(undefined, {
        timeZone: timeZone || undefined,
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  } catch {
    return { date: iso, time: "" };
  }
}
function parseIsoSafe(s) {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
function normalizePlate(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .slice(0, 24);
}
function normalizeFacility(s) {
  return String(s || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}
function toDateFromDnt(dateStr, timeStr) {
  const t = String(timeStr || "").match(/^\d{2}:\d{2}(:\d{2})?$/)
    ? timeStr.length === 5
      ? `${timeStr}:00`
      : timeStr
    : "00:00:00";
  const iso = `${String(dateStr || "").slice(0, 10)}T${t}`;
  return parseIsoSafe(iso);
}
function overlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
}

/* Small spinner */
function Spinner({ className = "" }) {
  return (
    <svg
      className={clsx("h-5 w-5 animate-spin", className)}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

/* Badges & pills */
function StatusBadge({ status }) {
  const map = {
    active: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
    },
    expired: { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-400" },
    pending: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium leading-none",
        s.bg,
        s.text
      )}
    >
      <span className={clsx("h-2 w-2 rounded-full", s.dot)} />{" "}
      {status[0].toUpperCase() + status.slice(1)}
    </span>
  );
}
function SourceBadge({ source }) {
  const map = {
    SpotHero: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-100" },
    ParkWhiz: {
      bg: "bg-indigo-50",
      text: "text-indigo-700",
      ring: "ring-indigo-100",
    },
  };
  const s = map[source] || {
    bg: "bg-slate-50",
    text: "text-slate-700",
    ring: "ring-slate-100",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium leading-none ring-1",
        s.bg,
        s.text,
        s.ring
      )}
    >
      <span className="h-2 w-2 rounded-full bg-current" /> {source} Reservation
    </span>
  );
}
function PlatePillSmall({ plate }) {
  if (!plate) return null;
  return (
    <div
      className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-white whitespace-nowrap leading-none"
      style={{ background: BLUE, boxShadow: `0 8px 20px ${BLUE}22` }}
      title={plate}
      aria-label={`License plate ${plate}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden
      >
        <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
        <path
          d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
          fill="currentColor"
        />
      </svg>
      <span
        className="font-mono tracking-wider"
        style={{ fontSize: "clamp(12px, 1.5vw, 14px)" }}
      >
        {plate}
      </span>
    </div>
  );
}
function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-slate-100",
    blue: "bg-sky-50 text-sky-700 ring-sky-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    rose: "bg-rose-50 text-rose-700 ring-rose-100",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium leading-none ring-1 ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/* Map ONE result row to a card shape */
function mapRowToCard(row, source = "SpotHero", status = "active") {
  return {
    facilityId: "",
    facilityName: row.facility_title || "Facility",
    licensePlate: row.license_plate || "",
    startsAt: row.starts,
    endsAt: row.ends,
    status: (status || "active").toLowerCase(),
    source,
    tz: row.facility_timezone || undefined,
    customer: {
      name: row.renter_full_name || "—",
      phone: row.renter_phone_number || "",
    },
  };
}

/* --- Icon: Ban --- */
function BanIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      {...props}
    >
      <circle cx="12" cy="12" r="8" />
      <path d="M8.5 8.5l7 7" />
    </svg>
  );
}

/* --- Badge: Do Not Tow --- */
function DoNotTowBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wide text-white shadow
                 ring-1 ring-rose-500/30"
      style={{
        background:
          "linear-gradient(135deg, rgba(244,63,94,1), rgba(225,29,72,1))", // rose-500 -> rose-600
        boxShadow: "0 8px 16px rgba(225,29,72,0.22)",
      }}
      aria-label="Do not tow"
    >
      <BanIcon className="h-3.5 w-3.5" />
      DO NOT TOW
    </span>
  );
}

/* --- Relocated block (with DNT badge) --- */
function RelocatedFacilityBlock({ dnt }) {
  if (!dnt) return null;
  const s = toDateFromDnt(dnt.start_date, dnt.start_time);
  const e = toDateFromDnt(dnt.end_date, dnt.end_time);
  const sTxt = s
    ? s.toLocaleString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  const eTxt = e
    ? e.toLocaleString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div className="mt-3 relative rounded-xl border border-amber-200 bg-amber-50 p-3">
      {/* Badge (top-right, stays visible on small screens) */}
      <div className="absolute right-2 top-2">
        <DoNotTowBadge />
      </div>

      <div className="mb-1 pr-28 text-xs font-semibold uppercase tracking-wide text-amber-900">
        {/* pr-28 to avoid overlap with the badge on narrow screens */}
        Relocated Facility
      </div>

      <div className="text-sm text-amber-900">
        <span className="font-medium">{dnt.relocated_facility_name}</span>
        {s && e ? (
          <span className="ml-1">
            ({s.toLocaleDateString()} {sTxt} → {e.toLocaleDateString()} {eTxt})
          </span>
        ) : null}
      </div>

      {(dnt.requester_name || dnt.requester_contact || dnt.reason) && (
        <div className="mt-1 text-xs text-amber-900/90">
          {dnt.requester_name ? (
            <span className="mr-2">By: {dnt.requester_name}</span>
          ) : null}
          {dnt.requester_contact ? (
            <span className="mr-2">Contact: {dnt.requester_contact}</span>
          ) : null}
          {/* Reason (optional) */}
        </div>
      )}
    </div>
  );
}

/* --- Reservation card (unchanged desktop design) --- */
function ReservationCard({ card, dntMatch }) {
  return (
    <article className="mt-4 rounded-2xl border border-black/[0.06] bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl text-white shadow shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 8px 16px ${BLUE}20`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4.5 w-4.5 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              Facility
            </div>
            <div className="truncate text-sm sm:text-base font-semibold leading-6 tracking-tight text-slate-800">
              {card.facilityName}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={card.status} />
        </div>
      </div>

      {/* times + plate */}
      <div
        className="
          mt-4 grid grid-cols-1 gap-3
          sm:grid-cols-2
          md:grid-cols-[1fr,1fr,220px]
          xl:grid-cols-[1fr,1fr,240px]
        "
      >
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            Start time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {fmtPartsTZ(card.startsAt, card.tz).date}
          </div>
          <div className="text-sm leading-5 text-slate-700">
            {fmtPartsTZ(card.startsAt, card.tz).time}
          </div>
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            End time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {fmtPartsTZ(card.endsAt, card.tz).date}
          </div>
          <div className="text-sm leading-5 text-slate-700">
            {fmtPartsTZ(card.endsAt, card.tz).time}
          </div>
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0 overflow-hidden sm:col-span-2 md:col-span-1">
          <div className="mb-2 text-[11px] sm:text-xs leading-4 text-slate-500">
            License Plate
          </div>
          <div className="flex w-full justify-start min-w-0">
            <div className="min-w-0">
              <PlatePillSmall plate={normalizePlate(card.licensePlate)} />
            </div>
          </div>
        </div>
      </div>

      {/* Relocated Facility (if matches) */}
      <RelocatedFacilityBlock dnt={dntMatch} />
    </article>
  );
}

/* Premium empty hero (shows until user searches) */
function EmptyHero() {
  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-black/[0.06] bg-gradient-to-br from-white via-white to-slate-50 shadow-sm"
      style={{ minHeight: "300px" }}
    >
      {/* gradient glow */}
      <div
        className="pointer-events-none absolute -top-20 -right-12 h-64 w-64 rounded-full blur-3xl opacity-30"
        style={{
          background: `radial-gradient(closest-side, ${BLUE}66, transparent)`,
        }}
      />
      <div
        className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full blur-3xl opacity-30"
        style={{
          background: `radial-gradient(closest-side, ${BLUE_LIGHT}66, transparent)`,
        }}
      />

      {/* subtle stars */}
      {[...Array(10)].map((_, i) => (
        <span
          key={i}
          className="absolute h-1 w-1 rounded-full bg-slate-300 animate-pulse"
          style={{
            top: `${10 + ((i * 7) % 80)}%`,
            left: `${5 + ((i * 11) % 90)}%`,
            opacity: 0.5,
            animationDelay: `${i * 0.25}s`,
          }}
        />
      ))}

      <div className="relative grid gap-6 p-6 sm:grid-cols-2 sm:p-8 lg:p-10">
        {/* text */}
        <div className="flex flex-col justify-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Find valid reservations fast
          </h2>
          <p className="mt-2 max-w-prose text-sm leading-6 text-slate-600">
            Search a license plate to view all matching reservations, live
            status, and any active
            <span className="font-medium"> Do Not Tow</span>{" "}
            relocations—optimized for on-site towing teams.
          </p>

          <ul className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
            <li className="flex items-center gap-2">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: BLUE }}
              />
              Instant multi-match results
            </li>
            <li className="flex items-center gap-2">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: BLUE }}
              />
              Relocation details when applicable
            </li>
          </ul>
        </div>

        {/* illustration */}
        <div className="flex items-center justify-center">
          <svg viewBox="0 0 360 220" className="w-full max-w-md">
            {/* road */}
            <rect
              x="0"
              y="160"
              width="360"
              height="60"
              rx="8"
              fill="#0f172a"
              opacity="0.08"
            />
            {/* parking slots */}
            {[0, 1, 2, 3].map((i) => (
              <rect
                key={i}
                x={30 + i * 75}
                y="40"
                width="60"
                height="90"
                rx="10"
                fill="#e5f0ff"
              />
            ))}
            {/* car */}
            <g transform="translate(45,55)">
              <rect width="56" height="36" rx="9" fill="#2a7efa" />
              <rect
                x="6"
                y="6"
                width="44"
                height="12"
                rx="3"
                fill="white"
                opacity="0.9"
              />
              <circle cx="16" cy="34" r="6" fill="#111827" />
              <circle cx="40" cy="34" r="6" fill="#111827" />
            </g>
            {/* tow truck */}
            <g transform="translate(210,140)">
              <rect
                width="80"
                height="24"
                rx="4"
                fill="#111827"
                opacity="0.85"
              />
              <rect
                x="10"
                y="-18"
                width="30"
                height="18"
                rx="3"
                fill="#94a3b8"
              />
              <rect
                x="45"
                y="-10"
                width="10"
                height="10"
                rx="2"
                fill="#475569"
              />
              <path d="M55,-10 L75,-26" stroke="#475569" strokeWidth="4" />
              <circle cx="16" cy="24" r="8" fill="white" />
              <circle cx="16" cy="24" r="5" fill="#111827" />
              <circle cx="60" cy="24" r="8" fill="white" />
              <circle cx="60" cy="24" r="5" fill="#111827" />
            </g>
            {/* plate badge */}
            <rect x="245" y="22" width="90" height="24" rx="6" fill="#eaf2ff" />
            <text
              x="255"
              y="39"
              fontFamily="monospace"
              fontSize="12"
              fill="#1e3a8a"
            >
              PLATE • CHECK
            </text>
          </svg>
        </div>
      </div>
    </section>
  );
}

function DntOnlyCard({ dnt }) {
  const s = toDateFromDnt(dnt.start_date, dnt.start_time);
  const e = toDateFromDnt(dnt.end_date, dnt.end_time);

  return (
    <article className="mt-4 rounded-2xl border border-black/[0.06] bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl text-white shadow shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 8px 16px ${BLUE}20`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4.5 w-4.5 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              Facility
            </div>
            <div className="truncate text-sm sm:text-base font-semibold leading-6 tracking-tight text-slate-800">
              {dnt.facility_name}
            </div>
          </div>
        </div>
        <DoNotTowBadge />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-[1fr,1fr,220px] xl:grid-cols-[1fr,1fr,240px]">
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            Start time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {s ? s.toLocaleDateString() : ""}
          </div>
          <div className="text-sm leading-5 text-slate-700">
            {s
              ? s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              : ""}
          </div>
        </div>
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            End time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {e ? e.toLocaleDateString() : ""}
          </div>
          <div className="text-sm leading-5 text-slate-700">
            {e
              ? e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              : ""}
          </div>
        </div>
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0 overflow-hidden sm:col-span-2 md:col-span-1">
          <div className="mb-2 text-[11px] sm:text-xs leading-4 text-slate-500">
            License Plate
          </div>
          <PlatePillSmall plate={normalizePlate(dnt.license_plate)} />
        </div>
      </div>

      {/* If it's a relocation, show the destination block */}
      {dnt.is_relocated && dnt.relocated_facility_name && (
        <RelocatedFacilityBlock dnt={dnt} />
      )}
    </article>
  );
}

/* Results header & list */
function LookupResult({
  loading,
  slow,
  error,
  data,
  plate,
  onRetry,
  dntItems,
}) {
  if (!plate) return null;

  const rows = data?.reservation?.results || [];
  const count = Number(rows.length || 0);
  const source = data?.source || "SpotHero";
  const status = (data?.status || "active").toLowerCase();
  const cards = rows.map((r) => mapRowToCard(r, source, status));

  function findDntMatchForCard(card) {
    if (!Array.isArray(dntItems) || dntItems.length === 0) return null;
    const plateNorm = normalizePlate(card.licensePlate);
    const facilityName = normalizeFacility(card.facilityName);
    const startL = parseIsoSafe(card.startsAt);
    const endL = parseIsoSafe(card.endsAt);
    return (
      dntItems.find((it) => {
        const plateOk = normalizePlate(it?.license_plate) === plateNorm;
        const facSrc = normalizeFacility(it?.facility_name);
        const facRel = normalizeFacility(it?.relocated_facility_name);
        const facilityOk =
          facSrc === facilityName ||
          (!!it?.relocated_facility_name && facRel === facilityName);
        const sD = toDateFromDnt(it?.start_date, it?.start_time);
        const eD = toDateFromDnt(it?.end_date, it?.end_time);
        const timeOk = overlap(startL, endL, sD, eD);
        return plateOk && facilityOk && timeOk;
      }) || null
    );
  }

  return (
    <div className="mt-4">
      {/* live header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-slate-600 truncate">
            Lookup result for
          </span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium text-white"
            style={{ background: BLUE }}
            title={plate}
          >
            {plate}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Chip tone="blue">Searching…</Chip>}

          {!loading && !error && count === 0 && <Chip>No matches</Chip>}
        </div>
      </div>

      {!loading && error && (
        <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <div className="min-w-0">{error}</div>
          <button
            onClick={onRetry}
            className="shrink-0 rounded-md bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:shadow"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="mt-4 grid place-items-center rounded-2xl border border-black/[0.06] bg-white p-10">
          <Loader size="lg" label={`Searching ${plate}…`} />
        </div>
      )}

      {!loading && !error && cards.length > 0 && (
        <div className="grid gap-4">
          {cards.map((c, idx) => (
            <ReservationCard
              key={`${c.licensePlate}-${c.startsAt}-${idx}`}
              card={c}
              dntMatch={findDntMatchForCard(c)}
            />
          ))}
        </div>
      )}

      {!loading &&
        !error &&
        cards.length === 0 &&
        Array.isArray(dntItems) &&
        dntItems.length > 0 && (
          <div className="grid gap-4">
            {dntItems
              .filter(
                (d) => normalizePlate(d.license_plate) === normalizePlate(plate)
              )
              .sort((a, b) => {
                const as =
                  toDateFromDnt(a.start_date, a.start_time)?.getTime() || 0;
                const bs =
                  toDateFromDnt(b.start_date, b.start_time)?.getTime() || 0;
                return bs - as;
              })
              .map((d) => (
                <DntOnlyCard key={`dnt-${d.id}`} dnt={d} />
              ))}
          </div>
        )}
    </div>
  );
}

export default function LandlordLookup() {
  const [query, setQuery] = useState("");
  const [searchPlate, setSearchPlate] = useState("");
  const [lookLoading, setLookLoading] = useState(false);
  const [lookSlow, setLookSlow] = useState(false);
  const [lookError, setLookError] = useState("");
  const [lookData, setLookData] = useState(null);
  const [dntItems, setDntItems] = useState([]);

  const lookupAbortRef = useRef(null);
  const dntAbortRef = useRef(null);

  async function performLookup(currentPlate) {
    if (lookupAbortRef.current) {
      lookupAbortRef.current.abort();
      lookupAbortRef.current = null;
    }
    if (dntAbortRef.current) {
      dntAbortRef.current.abort();
      dntAbortRef.current = null;
    }

    setLookError("");
    setLookData(null);
    setDntItems([]);

    if (!currentPlate) {
      setLookLoading(false);
      setLookSlow(false);
      return;
    }

    const controller = new AbortController();
    lookupAbortRef.current = controller;

    setLookLoading(true);
    setLookSlow(false);
    const slowTimer = setTimeout(() => setLookSlow(true), 1200);

    try {
      const res = await lookupPlateLandlord(currentPlate, {
        signal: controller.signal,
        timeout: 8000,
      });
      setLookData(res || {});

      // Fetch DNT once for the plate (active only)
      if (!controller.signal.aborted) {
        const dntCtl = new AbortController();
        dntAbortRef.current = dntCtl;

        const plateNorm = normalizePlate(currentPlate);
        const dntBody = await listDoNotTow(
          { active_only: true, limit: 200, search: plateNorm },
          { signal: dntCtl.signal, timeout: 15000 }
        ).catch(() => null);

        if (!dntCtl.signal.aborted && dntBody) {
          const items = Array.isArray(dntBody)
            ? dntBody
            : dntBody?.results || dntBody?.items || [];
          setDntItems(items);
        }
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setLookError(
          e?.payload?.message ||
            e?.message ||
            "Lookup failed. Please try again."
        );
      }
    } finally {
      clearTimeout(slowTimer);
      setLookLoading(false);
    }
  }

  // run only when user clicks Search
  useEffect(() => {
    if (!searchPlate) return;
    performLookup(searchPlate);
    return () => {
      if (lookupAbortRef.current) {
        lookupAbortRef.current.abort();
        lookupAbortRef.current = null;
      }
      if (dntAbortRef.current) {
        dntAbortRef.current.abort();
        dntAbortRef.current = null;
      }
    };
  }, [searchPlate]);

  function handleSearch() {
    const plate = normalizePlate(query);
    setSearchPlate(plate);
    if (!plate) {
      setLookData(null);
      setLookError("");
      setDntItems([]);
    }
  }
  function handleClear() {
    setQuery("");
    setSearchPlate("");
    setLookData(null);
    setLookError("");
    setDntItems([]);
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-3 py-4 sm:px-4 md:py-6">
      {/* Page heading */}
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
          Valid Reservations
        </h1>
      </header>

      {/* Top bar */}
      <div className="rounded-2xl border border-black/[0.06] bg-white/70 backdrop-blur px-4 py-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <div className="relative flex-1">
            <input
              id="plate"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              placeholder="Enter license plate (e.g. 3KKB36)"
              aria-busy={lookLoading ? "true" : "false"}
              className="w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 text-[15px] leading-5 text-slate-800 outline-none transition focus:ring-4"
              style={{
                ["--blue"]: BLUE,
                boxShadow: "inset 0 0 0 9999px transparent",
                borderColor: "rgba(0,0,0,.06)",
              }}
            />
            <div
              className={
                "absolute left-0 right-0 -bottom-px h-0.5 rounded-full transition-opacity " +
                (lookLoading ? "bg-[#2A7EFA] opacity-100" : "opacity-0")
              }
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-3 grid place-items-center text-slate-400"
              aria-hidden
            >
              {lookLoading ? (
                <Spinner className="h-5 w-5" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
              )}
            </div>
          </div>

          {/* Mobile small button; larger on md+ */}
          <div className="flex items-stretch gap-2">
            <button
              type="button"
              onClick={handleSearch}
              disabled={lookLoading}
              className="rounded-xl px-3 py-2 text-xs font-semibold text-white shadow transition disabled:opacity-60 sm:px-5 sm:py-3 sm:text-sm"
              style={{
                background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
                boxShadow: `0 10px 22px ${BLUE}22`,
              }}
            >
              Search
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50 sm:px-4 sm:py-3 sm:text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Either results or the hero */}
      {searchPlate ? (
        <LookupResult
          loading={lookLoading}
          slow={lookSlow}
          error={lookError}
          data={lookData}
          plate={searchPlate}
          onRetry={() => performLookup(searchPlate)}
          dntItems={dntItems}
        />
      ) : (
        <EmptyHero />
      )}
    </div>
  );
}
